import React, { useState, useEffect, useRef } from 'react';
import { LoadingOutlined, CloseCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { Button, Tooltip, Select, Switch } from 'antd';
import InputField from '../../../components/UI/InputField/InputField';
import {openNotification} from '../../../components/UI/Notification/Notification'
import { SELECT_DATA_ERROR, LOADING, COPIED_TO_CLIPBOARD, NO_SELECTED_VALUE } from '../../../shared/const/MessagesToUser/Messages';
import './SelectField.scss';
import { COPY_SELECTED_VALUE } from '../../../shared/const/Tittles/ToolTip';
import { convertCamelCaseToSpacedString, getSafe } from '../../../shared/utility';
import { useSelector } from 'react-redux';

const { Option } = Select;

const CANCEL_TOOLTIP = 'Cancel override';
const TEXT_BOX_TYPE = 'inputField';
const OVERRIDE_TEXT_BOX_PLACE_HOLDER = 'Type ingredient location here';
const OVERRIDE_BTN_TEXT = 'Override';
const FROM_OVERRIDE = true;

const SelectField = (props) => {
	const {
		data,
		copyToClipboardKey,
		toolTipKey,
		displayKey,
		valueKey,
		defaultValue,
		title,
		titlePosition,
		className,
		placeholder,
		optionsRender,
		onChange,
		multiple,
		showSearch,
		disabled,
		importData,
		selectFieldClass,
		isResetMode,
		onSwitchChange,
		switchDisabled,
		prevValue,
		allowClear,
		injectValue,
		toolTip,
		isResetValueWhenDisabled,
		isShowCopyToClipboard,
		width
	} = props;

	const [importValue, setImportValue] = useState(null);
	const [switchMode, setSwitchMode] = useState(onSwitchChange ? !props.defaultValueSkip : true);
	const [isOverrideBtnClicked, setIsOverrideBtnClicked] = useState(props.isOverride);
	const [selectedValue, setSelectedValue] = useState(null);

	// const dValue = useSelector((state) => defaultValueSelector ? getSafe(defaultValueSelector, state) : null);

	useEffect(() => {
		setIsOverrideBtnClicked(props.isOverride);
	}, [props.isOverride]);

	useEffect(() => {
		setSwitchMode(onSwitchChange ? !props.defaultValueSkip : true);
	}, [props.defaultValueSkip]);

	useEffect(() => {
		if (importData && data.length > 0) {
			setImportValue(importData);
			onSelectionChange(importData);
		} else {
			setImportValue(null);
		}
	}, [props.importData]);

	const onOverrideClick = () => {
		handleOverrideInputChange('', props.groupName);
		setIsOverrideBtnClicked(true);
	};

	const onCancelOverrideClick = () => {
		setIsOverrideBtnClicked(false);
		onChange(prevValue, FROM_OVERRIDE);
		setSelectedValue(prevValue);
	};

	const handleOverrideInputChange = (value, field) => {
		if (props.onOverrideChange) {
			props.onOverrideChange(value, field);
		}
	};

	const overrideModeDisplay = (fieldKey) => {
		return (
			<div className="override-input">
				<div className="input-fld-override">
					<InputField
						title=""
						value={props.isOverride && props.defaultValue ? props.defaultValue : null}
						defaultValue={props.isOverride && props.defaultValue ? props.defaultValue : null}
						placeholder={OVERRIDE_TEXT_BOX_PLACE_HOLDER}
						inputBox={TEXT_BOX_TYPE}
						onChange={(value) => {
							handleOverrideInputChange(value, fieldKey);
						}}
					/>
				</div>
				<div className="override-back-btn">
					<Tooltip title={CANCEL_TOOLTIP}>
						<CloseCircleOutlined
							onClick={() => {
								onCancelOverrideClick();
							}}
						/>
					</Tooltip>
				</div>
			</div>
		);
	};

	const onCopyToClipboard = () => {
		const selectedValueId = props.defaultValue || selectedValue;
		let foundSelectedValueObj = data.find((optionObj) => optionObj[valueKey] === selectedValueId);

		if (foundSelectedValueObj) {
			let foundSelectedValue = foundSelectedValueObj[copyToClipboardKey];
			navigator.clipboard.writeText(foundSelectedValue);
			openNotification(`${convertCamelCaseToSpacedString(copyToClipboardKey)} ${COPIED_TO_CLIPBOARD}`);
		} else {
			openNotification(NO_SELECTED_VALUE);
		}
	};

	const getOverrideButton = () => {
		return (
			<Button className="override-btn" onClick={() => onOverrideClick()}>
				{OVERRIDE_BTN_TEXT}
			</Button>
		);
	};

	const setOptions = () => {
		if (!!data) {
			try {
				let options = data.map((option, index) => {
					if (option[displayKey] !== '') {
						return (
							<Option
								value={option[valueKey]}
								key={index}
								label={option[displayKey]}
								title={option[toolTipKey] ? option[toolTipKey] : option[displayKey]}
							>
								{option[displayKey]}
							</Option>
						);
					}
				});
				return options;
			} catch (err) {
				console.warn(`${SELECT_DATA_ERROR}: ${title}`);
			}
		}
	};

	const getTitle = () => {
		if (title) {
			return (
				<div className="field-header">
					<Tooltip title={toolTip}>
						<div className={props.selectFieldTitleClass}>{title}</div>
					</Tooltip>
					<div className="flex-row">
						{isShowCopyToClipboard && !isOverrideBtnClicked && (
							<CopyOutlined
								className="margin-right-medium"
								title={`${COPY_SELECTED_VALUE} ${convertCamelCaseToSpacedString(copyToClipboardKey)}`}
								onClick={onCopyToClipboard}
							/>
						)}
						{onSwitchChange && (
							<Switch
								checked={switchMode}
								disabled={switchDisabled}
								checkedChildren="include"
								unCheckedChildren="skip"
								onChange={(checked) => switchChange(checked, false)}
							/>
						)}
					</div>
				</div>
			);
		}
	};

	const switchChange = (newSwitchMode) => {
		setSwitchMode(newSwitchMode);
		if (onSwitchChange) {
			onSwitchChange(newSwitchMode, props.selectKey, props.title);
		}
	};

	const onSelectionChange = (value) => {
		setSelectedValue(value);
		if (onChange) {
			onChange(value);
		}
	};

	const isShowOverrideButton = () => {
		return !!props.onOverrideChange && !isOverrideBtnClicked;
	};

	const selectRender = (menu) => {
		if (props.showLoading) {
			return (
				<div className="loading-area">
					<LoadingOutlined />
					<div>{LOADING}</div>
				</div>
			);
		} else {
			return (
				<div>
					{menu}
					{isShowOverrideButton() ? getOverrideButton() : null}
				</div>
			);
		}
	};

	const filterOption = (input, option) => {
		if (typeof option?.label == 'number') return (option?.label.toString() ?? '').includes(input);
		else return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
	};
	const generateSelectProps = () => {
		let selectProps = {
			className: `select-field ${titlePosition === 'left' && 'select-small'} ${!!selectFieldClass && selectFieldClass} `,
			onChange: onSelectionChange,
			mode: multiple ? 'multiple' : 'default',
			dropdownRender: selectRender,
			showSearch: true,
			disabled: disabled,
			allowClear: allowClear,
			filterOption: filterOption,
			value: selectedValue || undefined,
		};
		if (placeholder) {
			selectProps.placeholder = placeholder;
		}
		if (defaultValue) {
			selectProps.value = defaultValue;
		} else if (!!selectedValue) {
			selectProps.value = undefined;
		}
		if (importValue) {
			selectProps.value = importValue;
		}
		if (isResetValueWhenDisabled) {
			selectProps.value = null;
		}
		if (isResetMode) selectProps.value = null;
		if (data) {
			if (data.length === 0) {
				placeholder ? (selectProps.value = placeholder) : (selectProps.value = '');
			}
		}

		return selectProps;
	};

	const displaySelectOrOverrideMode = () => {
		if (isOverrideBtnClicked) {
			return overrideModeDisplay(props.groupName);
		} else {
			return switchMode && <Select {...generateSelectProps()}>{optionsRender ? optionsRender(data, displayKey) : setOptions()} </Select>;
		}
	};

	return (
		!props.hide && (
			<div className={`select-field-area ${titlePosition === 'left' ? 'select-with-left-title' : ''} ${className}`} style={{width: width}}>
				{getTitle()}
				{displaySelectOrOverrideMode()}
				{props.messageInfo && <label>{props.messageInfo}</label>}
			</div>
		)
	);
};

export default React.memo(SelectField);
